import * as React from "react";
import styled from "styled-components";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import { formatCompactNumber } from "../utils/formatCompactNumber";
import { TooltipBox, TooltipRow, TooltipDot, TooltipColon } from "./ChartTooltip";

const Caption = styled.p`
  margin: 20px 0px;
  padding-bottom: 10px;
  text-align: center;
  color: #666666;
`;

const LegendBox = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
`;

// The bordered box shrinks to its contents and is right-aligned, but items
// inside it flow from the left -- so a many-series legend fills the width in
// tidy left-aligned rows while a two-series one stays a small box on the right.
const LegendItems = styled.div`
  background: white;
  border: 1px solid #ccc;
  border-radius: 3px;
  padding: 8px 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 20px;
  font-size: 13px;
  font-weight: bold;
`;

const LegendItem = styled.div<{ dimmed: boolean }>`
  display: flex;
  align-items: center;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  opacity: ${(props) => (props.dimmed ? DIMMED_LEGEND_OPACITY : 1)};

  &:hover {
    text-decoration: underline;
  }
`;

interface Series {
  name: string;
  data: number[];
  color?: string;
}

interface DataSource {
  categories: string[];
  series: Series[];
}

const DEFAULT_COLOR = "#50b432";

interface Props {
  dataSource: DataSource;
  yAxisTitle?: string;
  valueSuffix?: string;
  valueDecimals?: number;
  caption?: string;
}

// Recharts' default 4px gap between bars in a group is a fixed cost, so with
// many series it eats the whole category band once the categories get dense
// (9 sites over 24 months leaves each bar under 1px, which antialiases away
// to near-white). A percentage is resolved against the band instead, so the
// gap shrinks with the bars and they stay above the sub-pixel cliff.
const BAR_GAP = "2%";

// How far the other series fade back when some are focused. Bars drop far
// enough to read as background texture, while legend labels stay legible --
// they have to be readable to be clicked back on.
const DIMMED_BAR_OPACITY = 0.07;
const DIMMED_LEGEND_OPACITY = 0.3;

// Room for the legend and the plot area itself. The x-axis label band is
// added on top of this (see rotatedAxisHeight below), so the chart grows
// with the labels rather than eating into the bars.
const BASE_CHART_HEIGHT = 340;
// X-axis labels get angled to avoid overlap once there are many categories
// (dense monthly data) or the category labels themselves are long (full
// site names) -- either can make horizontal labels collide.
const ROTATE_LABEL_COUNT_THRESHOLD = 10;
const ROTATE_LABEL_LENGTH_THRESHOLD = 15;
const ROTATE_LABEL_ANGLE_DEGREES = 45;
const AXIS_FONT_SIZE = 12;
const MIN_ROTATED_AXIS_HEIGHT = 60;
const MAX_ROTATED_AXIS_HEIGHT = 140;

// Beyond this many characters, rotated x-axis labels get truncated with an
// ellipsis rather than left to run into each other.
const MAX_ROTATED_LABEL_LENGTH = 18;

const truncateLabel = (label: string) =>
  label.length > MAX_ROTATED_LABEL_LENGTH
    ? `${label.slice(0, MAX_ROTATED_LABEL_LENGTH)}…`
    : label;

// The angled tick labels sit below the axis line, so their vertical extent
// (and therefore how much space needs to be reserved before the legend)
// grows with how long the longest category label is, not just a fixed
// height -- a handful of long site names need much more room than short
// month labels do. Measured against the truncated label, since that is what
// actually gets drawn.
const estimateRotatedAxisHeight = (categories: string[]) => {
  const longest = Math.max(
    0,
    ...categories.map((c) => truncateLabel(c).length)
  );
  const approxLabelWidth = longest * AXIS_FONT_SIZE * 0.6;
  const verticalExtent =
    approxLabelWidth * Math.sin((ROTATE_LABEL_ANGLE_DEGREES * Math.PI) / 180);
  return Math.min(
    MAX_ROTATED_AXIS_HEIGHT,
    Math.max(MIN_ROTATED_AXIS_HEIGHT, verticalExtent + 20)
  );
};

interface LegendEntry {
  value: string;
  color?: string;
}

// With nothing focused every series is shown at full strength; once any are
// picked, the rest fade back.
const isDimmed = (focused: string[], name: string) =>
  focused.length > 0 && !focused.includes(name);

const renderLegend = (
  focused: string[],
  onToggle: (name: string) => void
) => (props: { payload?: LegendEntry[] }) => {
  const { payload } = props;
  if (!payload || !payload.length) return null;
  return (
    <LegendBox>
      <LegendItems>
        {payload.map((entry) => (
          <LegendItem
            key={entry.value}
            dimmed={isDimmed(focused, entry.value)}
            onClick={() => onToggle(entry.value)}
          >
            <TooltipDot dotColor={entry.color || DEFAULT_COLOR} />
            {entry.value}
          </LegendItem>
        ))}
      </LegendItems>
    </LegendBox>
  );
};

interface TooltipContentProps extends TooltipProps<number, string> {
  valueFormatter: (value: number) => string;
  focused: string[];
}

const CustomTooltip: React.FC<TooltipContentProps> = ({
  active,
  payload,
  label,
  valueFormatter,
  focused,
}) => {
  if (!active || !payload || !payload.length) return null;

  // Focusing a series is a request to look at just that one, so the tooltip
  // narrows to match rather than listing every site alongside it.
  const rows =
    focused.length > 0
      ? payload.filter((entry) => focused.includes(String(entry.dataKey)))
      : payload;
  if (!rows.length) return null;

  return (
    <TooltipBox>
      <div>{label}</div>
      {rows.map((entry) => (
        <TooltipRow key={entry.dataKey as string}>
          <TooltipDot dotColor={entry.color || DEFAULT_COLOR} />
          <span>{entry.name}</span>
          <TooltipColon>:</TooltipColon>
          <strong>{valueFormatter(entry.value as number)}</strong>
        </TooltipRow>
      ))}
    </TooltipBox>
  );
};

export const MultiSeriesColumnChart: React.FC<Props> = ({
  dataSource,
  yAxisTitle,
  valueSuffix = "",
  valueDecimals = 0,
  caption,
}) => {
  const { categories, series } = dataSource;
  const [focused, setFocused] = React.useState<string[]>([]);

  const rows = React.useMemo(
    () =>
      categories.map((category, index) => {
        const row: Record<string, string | number> = { category };
        series.forEach((s) => {
          row[s.name] = s.data[index] ?? 0;
        });
        return row;
      }),
    [categories, series]
  );

  const formatValue = (value: number) =>
    `${value.toFixed(valueDecimals)}${valueSuffix}`;

  const longestCategoryLength = Math.max(
    0,
    ...categories.map((c) => c.length)
  );
  const rotateLabels =
    categories.length > ROTATE_LABEL_COUNT_THRESHOLD ||
    longestCategoryLength > ROTATE_LABEL_LENGTH_THRESHOLD;
  const rotatedAxisHeight = rotateLabels
    ? estimateRotatedAxisHeight(categories)
    : 30;
  const chartHeight = BASE_CHART_HEIGHT + rotatedAxisHeight;

  // Clicking a legend entry or a bar adds that series to the focused set and
  // fades the rest; clicking it again drops it back out. Emptying the set
  // returns every series to full strength.
  const toggleFocus = React.useCallback((name: string) => {
    setFocused((current) =>
      current.includes(name)
        ? current.filter((series) => series !== name)
        : [...current, name]
    );
  }, []);

  return (
    <div>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          data={rows}
          barGap={BAR_GAP}
          margin={{
            top: 10,
            right: 20,
            left: 10,
            // The label band is reserved by the XAxis `height` below, which
            // Recharts already adds to the plot offset -- repeating it here
            // would reserve the same space twice.
            bottom: 5,
          }}
        >
          <CartesianGrid vertical={false} stroke="#e6e6e6" />
          <XAxis
            dataKey="category"
            tick={{ fontSize: AXIS_FONT_SIZE }}
            tickFormatter={rotateLabels ? truncateLabel : undefined}
            interval={0}
            angle={rotateLabels ? -ROTATE_LABEL_ANGLE_DEGREES : 0}
            textAnchor={rotateLabels ? "end" : "middle"}
            height={rotatedAxisHeight}
          />
          <YAxis
            tick={{ fontSize: 13 }}
            tickFormatter={formatCompactNumber}
            label={
              yAxisTitle
                ? {
                    value: yAxisTitle,
                    angle: -90,
                    position: "insideLeft",
                    style: { fontSize: 13, textAnchor: "middle" },
                  }
                : undefined
            }
          />
          <Tooltip
            content={
              <CustomTooltip valueFormatter={formatValue} focused={focused} />
            }
            cursor={{ fill: "rgba(0,0,0,0.05)" }}
          />
          <Legend
            content={renderLegend(focused, toggleFocus)}
            verticalAlign="top"
            align="right"
          />
          {series.map((s) => (
            <Bar
              key={s.name}
              dataKey={s.name}
              fill={s.color || DEFAULT_COLOR}
              fillOpacity={isDimmed(focused, s.name) ? DIMMED_BAR_OPACITY : 1}
              cursor="pointer"
              onClick={() => toggleFocus(s.name)}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
      {caption && <Caption>{caption}</Caption>}
    </div>
  );
};
